import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Badge,
  Typography,
  Divider,
  Table,
  Avatar,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  TagOutlined,
  NumberOutlined,
  TeamOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const ClubDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);

  // API hooks
  const { execute: getClub, loading: clubLoading } = useDynamicApi("GET");

  // Load club details
  const loadClubDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.CLUBS_UPDATE}/${id}`;
      const response = await getClub(endpoint);

      if (response?.status === true && response.data) {
        setClub(response.data);
      } else {
        Toast.error(t("clubManager.failedFetchDetails"));
        navigate("/clubs");
      }
    } catch (error) {
      console.error("Error fetching club details:", error);
      Toast.error(error.message || t("clubManager.failedFetchDetails"));
      navigate("/clubs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadClubDetails();
    }
  }, [id]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get user type color
  const getUserTypeColor = (userType) => {
    const colors = {
      CLUB_COACH: "green",
      CLUB_ADMIN: "blue",
      INDIVIDUAL: "purple",
      KNKV_PARTICIPANT: "orange",
    };
    return colors[userType] || "default";
  };

  // Format user type for display
  const formatUserType = (userType) => {
    if (!userType) return "N/A";
    const userTypeLabels = {
      CLUB_COACH: t("userManager.clubCoach"),
      CLUB_ADMIN: t("userManager.clubAdmin"),
      INDIVIDUAL: t("userManager.individual"),
      KNKV_PARTICIPANT: t("userManager.knkvParticipant"),
      KNKV_INSTRUCTOR: t("userManager.knkvInstructor"),
      SYSTEM_ADMIN: t("userManager.systemAdmin"),
    };
    return userTypeLabels[userType] || userType;
  };

  // Navigate to user details
  const handleViewUser = (userId) => {
    navigate(`/users/details/${userId}`);
  };

  // Members table columns
  const memberColumns = [
    {
      title: t("clubManager.member") || "Member",
      key: "member",
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.full_name}</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              ID: {record.id}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: t("common.email"),
      key: "email",
      render: (_, record) => (
        <Space>
          <MailOutlined style={{ color: "#1890ff" }} />
          <Text>{record.email}</Text>
        </Space>
      ),
    },
    {
      title: t("userManager.mobile"),
      key: "mobile_number",
      render: (_, record) => (
        <Space>
          <PhoneOutlined style={{ color: "#52c41a" }} />
          <Text>{record.mobile_number || "N/A"}</Text>
        </Space>
      ),
    },
    {
      title: t("userManager.userType"),
      key: "user_type",
      render: (_, record) => (
        <Tag color={getUserTypeColor(record.user_type)}>
          <IdcardOutlined style={{ marginRight: "4px" }} />
          {formatUserType(record.user_type)}
        </Tag>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title={t("common.view")}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewUser(record.id)}
            style={{ backgroundColor: "#1890ff" }}
          >
            {t("common.view")}
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (loading || clubLoading) return null;

  if (!club) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Title level={3}>{t("clubManager.noClubsFound")}</Title>
          <Button type="primary" onClick={() => navigate("/clubs")}>
            {t("common.previous")} {t("clubManager.title")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="club-details-page">
      {/* Header with back button and actions */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/clubs")}
          >
            {t("common.previous")} {t("clubManager.title")}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t("clubManager.clubDetails")}
          </Title>
        </Space>
      </div>

      {/* Main Content */}
      <Card>
        {/* Avatar and Title Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "#f0f2f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "24px",
            }}
          >
            <TeamOutlined style={{ fontSize: "40px", color: "#8c8c8c" }} />
          </div>
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>{club.name}</h1>
            <Space size="middle">
              <Badge
                status={club.is_active ? "success" : "default"}
                text={
                  club.is_active ? t("common.active") : t("common.inactive")
                }
              />
              <Text type="secondary">
                <TeamOutlined /> {club.members?.length || 0}{" "}
                {t("clubManager.members") || "Members"}
              </Text>
            </Space>
          </div>
        </div>

        {/* Club Details in Descriptions */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("clubManager.clubName")} span={2}>
            <Space>
              <TagOutlined />
              {club.name}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.description")} span={2}>
            <div
              style={{
                whiteSpace: "pre-wrap",
                backgroundColor: "#f5f5f5",
                padding: "12px",
                borderRadius: "4px",
                minHeight: "60px",
              }}
            >
              {club.description ||
                t("clubManager.noDescription") ||
                "No description provided"}
            </div>
          </Descriptions.Item>

          <Descriptions.Item
            label={t("clubManager.clubId") || "Club ID"}
            span={1}
          >
            {club.id}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("clubManager.displayOrder") || "Display Order"}
            span={1}
          >
            {club.display_order || 0}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("clubManager.createdBy") || "Created By"}
            span={1}
          >
            <Space>
              <UserOutlined />
              {club.created_by_email || "N/A"}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.status")} span={1}>
            <Tag color={club.is_active ? "green" : "default"}>
              {club.is_active ? t("common.active") : t("common.inactive")}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.createdAt")} span={1}>
            <Space>
              <CalendarOutlined />
              {formatDate(club.created_at)}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.lastUpdated")} span={1}>
            <Space>
              <ClockCircleOutlined />
              {formatDate(club.updated_at)}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        {/* Members Section */}
        <div style={{ marginTop: "24px" }}>
          <Divider orientation="left">
            <Space>
              <TeamOutlined />
              {t("clubManager.members") || "Club Members"} (
              {club.members?.length || 0})
            </Space>
          </Divider>

          {club.members && club.members.length > 0 ? (
            <Table
              dataSource={club.members}
              columns={memberColumns}
              rowKey="id"
              pagination={false}
              bordered
              size="middle"
              scroll={{ x: 1000 }}
            />
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                backgroundColor: "#fafafa",
                borderRadius: "8px",
              }}
            >
              <TeamOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
              <Title level={4} style={{ marginTop: "16px", color: "#8c8c8c" }}>
                {t("clubManager.noMembersFound") || "No Members Found"}
              </Title>
              <Text type="secondary">
                {t("clubManager.noMembersMessage") ||
                  "This club doesn't have any members yet."}
              </Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ClubDetails;
